import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { isProviderUser } from '../../util/onboarding';
import { LEGAL_DOCS } from '../../config/legal';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import FooterContainer from '../FooterContainer/FooterContainer';
import ProviderTopbar from '../ProviderTopbar/ProviderTopbar';
import LegalContent from './LegalContent';
import LEGAL_CONTENT from './content';
import css from './LegalPage.module.css';

// Legal/About pages are self-contained content pages reachable by anyone, so the chrome adapts to who's
// looking: an anonymous visitor gets the public browse topbar (same as the homepage, search and class
// pages); a signed-in parent gets their own app topbar; a provider gets a minimal header carrying their
// dashboard menu (they have no parent topbar).
export const chromeForRole = ({ isAuthenticated, currentUser }) => {
  // The legal footer (About + every legal doc) is shown to EVERYONE on these pages (client ask),
  // so `always` forces it even for logged-out visitors; only the TOPBAR varies by role.
  const footer = <FooterContainer inHub always />;
  if (isAuthenticated && isProviderUser(currentUser)) {
    return { topbar: <ProviderTopbar />, footer };
  }
  // Anonymous visitors, signed-in parents (and any other authed userType, e.g. admin) get the standard
  // topbar -- it adapts to auth state itself (Log in / Sign up vs the profile menu).
  return { topbar: <TopbarContainer />, footer };
};

/**
 * Renders one in-repo legal/About document. The `documentId` is supplied by the route (see
 * routeConfiguration.js) and selects both the metadata (title/version/effective date, from
 * src/config/legal.js) and the content blocks (src/containers/LegalPage/content).
 */
export const LegalPageComponent = props => {
  const intl = useIntl();
  const { documentId, scrollingDisabled, isAuthenticated, currentUser } = props;

  const doc = LEGAL_DOCS[documentId];
  const blocks = LEGAL_CONTENT[documentId];
  const { topbar, footer } = chromeForRole({ isAuthenticated, currentUser });

  // Guard against a mis-wired route: render nothing meaningful rather than crash.
  if (!doc || !blocks) {
    return (
      <Page title="KidHive" scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn topbar={topbar} footer={footer}>
          <div className={css.content} />
        </LayoutSingleColumn>
      </Page>
    );
  }

  const title = `KidHive | ${doc.title}`;
  const metaLine = doc.version
    ? intl.formatMessage(
        { id: 'LegalPage.revisionLine' },
        { version: doc.version, effectiveDate: doc.effectiveDate }
      )
    : null;

  return (
    <Page title={title} description={doc.title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn mainColumnClassName={css.layoutMain} topbar={topbar} footer={footer}>
        <article className={css.content}>
          <header className={css.docHeader}>
            <h1 className={css.h1}>{doc.title}</h1>
            {metaLine ? <p className={css.metaLine}>{metaLine}</p> : null}
          </header>

          <div className={css.body}>
            <LegalContent blocks={blocks} />
          </div>

        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  isAuthenticated: state.auth.isAuthenticated,
  currentUser: state.user.currentUser,
});

const LegalPage = compose(connect(mapStateToProps))(LegalPageComponent);

export default LegalPage;
